// components/SizeOptionsModal.jsx
//
// Center-modal editor for a commodity's Size Options (Size Options Overhaul, M2).
// Two ordered pill editors — Domestic and Import — share the OrderedSizeEditor:
//   • add    — type a size, press Enter or the ＋ button
//   • remove — click a pill to arm it (reveals ✕), click ✕ to remove
//   • reorder— hover (or focus) a pill to reveal ‹ › arrows; click to move
// Saving sends the full ordered set for the commodity to the transactional
// PUT /api/sizeoptions/commodity/:id reconcile endpoint (both origins at once).
import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { X, Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import authAxios from '../utils/authAxios';
import { Input, Select, Button, DangerButton, SecondaryButton } from '../styles/TableStyles';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: var(--shadow-a35);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Dialog = styled.div`
  background: var(--color-surface);
  border-radius: 16px;
  box-shadow: 0 24px 64px var(--shadow-a25);
  width: min(560px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 22px 22px 18px;
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-a60);
  cursor: pointer;

  &:hover { background: var(--shadow-a05); color: var(--black); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
`;

const Title = styled.h2`
  margin: 0 0 18px;
  font-size: 1.1rem;
  color: var(--green-800);
  font-weight: 700;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-a70);
  margin-bottom: 6px;
`;

const CommodityStatic = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--green-800);
`;

// Each section scopes its own --so-* custom properties; descendant pills/buttons
// read them, so the Domestic/Import colorway is set in one place per origin.
const originVars = (origin) => origin === 'import'
  ? css`
      --so-bg: var(--orange-a30);
      --so-text: var(--black);
      --so-border: var(--orange-a60);
      --so-rail: var(--orange-500);
    `
  : css`
      --so-bg: var(--green-a15);
      --so-text: var(--green-800);
      --so-border: var(--green-a60);
      --so-rail: var(--green-700);
    `;

const Section = styled.section`
  ${({ $origin }) => originVars($origin)}
  border: 1px solid var(--shadow-a08);
  border-left: 4px solid var(--so-rail);
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 14px;
  background: var(--color-surface);
`;

const SectionTitle = styled.div`
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--so-text);
  margin-bottom: 11px;
`;

const PillsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 38px;
  align-items: center;
  margin-bottom: 12px;
`;

const Empty = styled.span`
  font-size: 0.82rem;
  color: var(--ink-a60);
  font-style: italic;
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  border: 1px solid var(--so-border);
  background: var(--so-bg);
  border-radius: 9px;
  transition: box-shadow 0.14s ease, border-color 0.14s ease;

  &:hover { box-shadow: 0 2px 8px var(--shadow-a15); }
  &.armed { border-color: var(--color-danger); }
`;

const Arrow = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  color: var(--so-text);
  width: 0;
  opacity: 0;
  padding: 0;
  overflow: hidden;
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: width 0.14s ease, opacity 0.14s ease, background 0.12s ease;

  ${Pill}:hover &,
  ${Pill}:focus-within & { width: 22px; opacity: 1; }
  &:hover:not(:disabled) { background: var(--shadow-a08); }
  &:disabled { opacity: 0.25; cursor: default; }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: -2px; }
  /* Armed wins over hover: hide the arrows while a pill is staged for removal. */
  ${Pill}.armed & { width: 0; opacity: 0; }
`;

const Val = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  color: var(--so-text);
  font-family: inherit;
  font-weight: 700;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
  padding: 5px 10px;
  user-select: none;

  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: -2px; }
`;

const RemoveX = styled.button`
  border: 0;
  cursor: pointer;
  color: var(--white);
  background: var(--color-danger);
  width: 0;
  opacity: 0;
  padding: 0;
  overflow: hidden;
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: width 0.14s ease, opacity 0.14s ease;

  ${Pill}.armed & { width: 26px; opacity: 1; }
  &:focus-visible { outline: 2px solid var(--color-danger); outline-offset: -2px; }
`;

const AddRow = styled.div`
  display: flex;
  gap: 8px;
`;

const AddBtn = styled.button`
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  background: var(--green-700);
  color: var(--white);
  width: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.14s ease;

  &:hover { opacity: 0.9; }
  &:focus-visible { outline: 2px solid var(--green-900); outline-offset: 2px; }
`;

const ErrorText = styled.p`
  margin: 0 0 12px;
  color: var(--color-danger);
  font-size: 0.85rem;
  font-weight: 600;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;

  .spacer { flex: 1; }
`;

// Ordered, removable, addable pill list for one origin.
function OrderedSizeEditor({ origin, sizes, onChange }) {
  const [draft, setDraft] = useState('');
  const [armed, setArmed] = useState(-1);

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (!sizes.includes(v)) onChange([...sizes, v]);
    setDraft('');
  };
  const remove = (i) => {
    onChange(sizes.filter((_, idx) => idx !== i));
    setArmed(-1);
  };
  const move = (i, j) => {
    if (j < 0 || j >= sizes.length) return;
    const next = sizes.slice();
    const [item] = next.splice(i, 1);
    next.splice(j, 0, item);
    onChange(next);
    setArmed(-1);
  };

  return (
    <>
      <PillsWrap>
        {sizes.length === 0 ? (
          <Empty>
            {origin === 'import'
              ? 'No import sizes — add one to create an Import sub-group.'
              : 'No sizes yet — add the first below.'}
          </Empty>
        ) : (
          sizes.map((val, i) => (
            <Pill key={`${val}-${i}`} className={armed === i ? 'armed' : ''}>
              <Arrow
                type="button"
                aria-label={`Move ${val} earlier`}
                disabled={i === 0}
                onClick={() => move(i, i - 1)}
              >
                <ChevronLeft size={15} aria-hidden="true" />
              </Arrow>
              <Val
                type="button"
                aria-label={`${val} — click to remove`}
                onClick={() => setArmed(armed === i ? -1 : i)}
              >
                {val}
              </Val>
              <RemoveX type="button" aria-label={`Remove ${val}`} onClick={() => remove(i)}>
                <X size={13} aria-hidden="true" />
              </RemoveX>
              <Arrow
                type="button"
                aria-label={`Move ${val} later`}
                disabled={i === sizes.length - 1}
                onClick={() => move(i, i + 1)}
              >
                <ChevronRight size={15} aria-hidden="true" />
              </Arrow>
            </Pill>
          ))
        )}
      </PillsWrap>
      <AddRow>
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={origin === 'import' ? 'Add an import size…' : 'Add a size…'}
          aria-label={origin === 'import' ? 'Add an import size' : 'Add a domestic size'}
          style={{ flex: 1 }}
        />
        <AddBtn type="button" onClick={add} aria-label={origin === 'import' ? 'Add import size' : 'Add domestic size'}>
          <Plus size={18} aria-hidden="true" />
        </AddBtn>
      </AddRow>
    </>
  );
}

export default function SizeOptionsModal({
  open,
  mode = 'edit', // 'add' | 'edit'
  commodity, // { UniqueID, Commodity } | null
  initialDomestic = [],
  initialImport = [],
  commodities = [], // [{ UniqueID, Commodity }] — for the Add picker
  busy = false,
  onClose,
  onSaved,
  onRequestDelete,
}) {
  const [selectedId, setSelectedId] = useState('');
  const [domestic, setDomestic] = useState([]);
  const [imp, setImp] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedId(commodity ? String(commodity.UniqueID) : '');
    setDomestic(Array.isArray(initialDomestic) ? initialDomestic.slice() : []);
    setImp(Array.isArray(initialImport) ? initialImport.slice() : []);
    setSaving(false);
    setError('');
  }, [open, commodity, initialDomestic, initialImport]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const resolved = useMemo(() => {
    if (mode === 'edit') return commodity || null;
    return commodities.find((c) => String(c.UniqueID) === String(selectedId)) || null;
  }, [mode, commodity, commodities, selectedId]);

  if (!open) return null;

  const handleSave = async () => {
    if (!resolved) { setError('Select a commodity first.'); return; }
    if (domestic.length === 0) { setError('Add at least one Domestic size.'); return; }
    setError('');
    setSaving(true);
    try {
      await authAxios.put(`/api/sizeoptions/commodity/${resolved.UniqueID}`, {
        commodity: resolved.Commodity,
        domestic,
        import: imp,
      });
      setSaving(false);
      onSaved && onSaved();
    } catch (err) {
      console.error('Save size options failed', err);
      setError(err?.response?.data?.error || 'Could not save size options.');
      setSaving(false);
    }
  };

  const disabled = saving || busy;

  return (
    <Backdrop onClick={onClose} role="presentation">
      <Dialog onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="so-modal-title">
        <CloseBtn type="button" onClick={onClose} aria-label="Close editor">
          <X size={16} aria-hidden="true" />
        </CloseBtn>

        <Title id="so-modal-title">{mode === 'edit' ? 'Edit Size Options' : 'Add Size Options'}</Title>

        <Field>
          <Label htmlFor={mode === 'add' ? 'so-commodity' : undefined}>Commodity</Label>
          {mode === 'edit' ? (
            <CommodityStatic>{commodity?.Commodity}</CommodityStatic>
          ) : (
            <Select id="so-commodity" value={selectedId} onChange={(e) => setSelectedId(e.target.value)} style={{ width: '100%' }}>
              <option value="">Select a commodity…</option>
              {commodities.map((c) => (
                <option key={c.UniqueID} value={c.UniqueID}>{c.Commodity}</option>
              ))}
            </Select>
          )}
        </Field>

        <Section $origin="domestic">
          <SectionTitle>Domestic Sizes</SectionTitle>
          <OrderedSizeEditor origin="domestic" sizes={domestic} onChange={setDomestic} />
        </Section>

        <Section $origin="import">
          <SectionTitle>Import Sizes</SectionTitle>
          <OrderedSizeEditor origin="import" sizes={imp} onChange={setImp} />
        </Section>

        {error && <ErrorText>{error}</ErrorText>}

        <Foot>
          {mode === 'edit' && (
            <DangerButton type="button" onClick={() => onRequestDelete && onRequestDelete(commodity)} disabled={disabled}>
              <Trash2 size={15} aria-hidden="true" /> Delete
            </DangerButton>
          )}
          <span className="spacer" />
          <SecondaryButton type="button" onClick={onClose} disabled={saving}>Cancel</SecondaryButton>
          <Button type="button" onClick={handleSave} disabled={disabled}>{saving ? 'Saving…' : 'Save'}</Button>
        </Foot>
      </Dialog>
    </Backdrop>
  );
}
